import { dec, gt, gte, lt, max, mul, ZERO, type Decimal } from '../core/decimal.ts';
import type { Goal, GoalOperator, GoalSeries } from '../types/domain.ts';

const NET_WORTH_MILESTONES: readonly Decimal[] = [
  10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 2_000_000, 5_000_000,
].map((value) => dec(value));

/** Monthly passive income milestones. */
const PASSIVE_INCOME_MILESTONES: readonly Decimal[] = [500, 1_000, 2_000, 3_000, 5_000, 10_000].map((value) => dec(value));

const STRETCH_FLOOR = dec(100_000);
const INVESTMENT_TARGET_AFTER_CASH = dec(50_000);

/**
 * SmartGoalSuggester.SuggestedGoal without its localised label and reason, which are UI copy
 * and not part of the contract.
 */
export interface SuggestedGoal {
  readonly series: GoalSeries;
  readonly operation: GoalOperator;
  readonly targetValue: Decimal;
  readonly priority: number;
}

export interface SuggestionInputs {
  readonly currentNetWorth: Decimal;
  readonly currentDebt: Decimal;
  readonly currentCash: Decimal;
  readonly monthlyExpenses: Decimal | null;
  readonly existingGoals: readonly Goal[];
  readonly includeStretchGoals: boolean;
}

const suggestion = (series: GoalSeries, operation: GoalOperator, targetValue: Decimal, priority: number): SuggestedGoal => ({
  series,
  operation,
  targetValue,
  priority,
});

const hasGoalFor = (series: GoalSeries, goals: readonly Goal[]): boolean => goals.some((goal) => goal.series === series);

const nextMilestone = (current: Decimal): Decimal | null => NET_WORTH_MILESTONES.find((m) => gt(m, current)) ?? null;

/** A milestone at least max(2 × next milestone, 100K) away. */
const stretchMilestone = (current: Decimal): Decimal | null => {
  const target = max(mul(nextMilestone(current) ?? ZERO, dec(2)), STRETCH_FLOOR);
  return NET_WORTH_MILESTONES.find((m) => gte(m, target)) ?? null;
};

const nextPassiveMilestone = (current: Decimal): Decimal | null =>
  PASSIVE_INCOME_MILESTONES.find((m) => gt(m, current)) ?? null;

const debtFree = ({ currentDebt, existingGoals }: SuggestionInputs): SuggestedGoal | null =>
  gt(currentDebt, ZERO) && !hasGoalFor('debt', existingGoals) ? suggestion('debt', 'crossunder', ZERO, 0) : null;

const emergencyFund = ({ monthlyExpenses, currentCash, existingGoals }: SuggestionInputs): SuggestedGoal | null => {
  if (monthlyExpenses === null || !gt(monthlyExpenses, ZERO)) return null;
  const sixMonths = mul(monthlyExpenses, dec(6));
  return lt(currentCash, sixMonths) && !hasGoalFor('cash', existingGoals)
    ? suggestion('cash', 'crossover', sixMonths, 1)
    : null;
};

const netWorthMilestone = ({ currentNetWorth, existingGoals }: SuggestionInputs): SuggestedGoal | null => {
  if (hasGoalFor('netWorth', existingGoals)) return null;
  const target = nextMilestone(currentNetWorth);
  return target === null ? null : suggestion('netWorth', 'crossover', target, 2);
};

const passiveIncome = ({ includeStretchGoals, existingGoals }: SuggestionInputs): SuggestedGoal | null => {
  if (!includeStretchGoals || hasGoalFor('passive', existingGoals)) return null;
  const target = nextPassiveMilestone(ZERO);
  return target === null ? null : suggestion('passive', 'crossover', target, 3);
};

const stretchNetWorth = (inputs: SuggestionInputs, earlier: readonly SuggestedGoal[]): SuggestedGoal | null => {
  if (!inputs.includeStretchGoals) return null;
  if (hasGoalFor('netWorth', inputs.existingGoals) || earlier.some((s) => s.series === 'netWorth')) return null;
  const target = stretchMilestone(inputs.currentNetWorth);
  return target === null ? null : suggestion('netWorth', 'crossover', target, 4);
};

const present = <T>(value: T | null): value is T => value !== null;

/**
 * SmartGoalSuggester.generateSuggestions: debt-free, 6-month emergency fund, next net-worth
 * milestone, and (stretch) 500/month passive income and a far net-worth milestone, skipping
 * series the user already tracks; sorted by priority (stable).
 */
export function generateGoalSuggestions(inputs: SuggestionInputs): readonly SuggestedGoal[] {
  const core = [debtFree(inputs), emergencyFund(inputs), netWorthMilestone(inputs), passiveIncome(inputs)].filter(present);
  const all = [...core, stretchNetWorth(inputs, core)].filter(present);
  return [...all].sort((a, b) => a.priority - b.priority);
}

/**
 * SmartGoalSuggester.suggestNextGoal: after debt, the next milestone above current net worth;
 * after net worth, the next milestone above the achieved target; after cash, 50K invested.
 */
export function suggestNextGoal(achievedGoal: Goal, currentNetWorth: Decimal): SuggestedGoal | null {
  switch (achievedGoal.series) {
    case 'debt':
    case 'netWorth': {
      const from = achievedGoal.series === 'debt' ? currentNetWorth : achievedGoal.targetValue;
      const target = nextMilestone(from);
      return target === null ? null : suggestion('netWorth', 'crossover', target, 0);
    }
    case 'cash':
      return suggestion('investment', 'crossover', INVESTMENT_TARGET_AFTER_CASH, 0);
    default:
      return null;
  }
}
